'use strict';

import { DOM } from '../helpers/DOM.js';
import { Helper } from '../helpers/Helper.js';

export class Checkout {
	#levcharity_donation_checkout_fields;
	#levcharity_donation_checkout_ajax_fields;
	#search_team_field;
	#admin_ajax;
	#donation_frequency_monthly_input;
	#donation_installment_input;
	#checkoutForm;
	#currentCurrency = '';

	//variables for managing fetch requests
	#is_fetching = false;
	#is_fetching_predifined_amount = false;
	#is_fetching_donation_currency = false;
	#fieldDebounceTimers = {};
	#pending_requests = 0;

	//Team Campaign
	#team_and_participant_results;
	#search_team_and_participant_input;

	#isUpdating = false;
	// Gift Aid
	#gift_aid_wrapper;

	#dafValidationFields = {
		payment_method: false,
		billing_first_name: false,
		billing_last_name: false,
		billing_address_1: false,
		billing_city: false,
		billing_postcode: false,
		billing_state: false,
		billing_country: false,
		billing_email: false,
		billing_phone: false,
	};

	constructor() {
		//Add 'levcharity-donation-checkout-ajax-field' class to your element for ajax requests
		this.#levcharity_donation_checkout_fields = document.querySelector(
			'.levcharity-donation-checkout-fields'
		);

		this.#levcharity_donation_checkout_ajax_fields =
			document.querySelectorAll(
				'.levcharity-donation-checkout-ajax-field'
			);
		this.#search_team_field = document.querySelector(
			'#search_team_and_participant'
		);
		this.#admin_ajax = admin_ajax[ 0 ];
		this.#donation_frequency_monthly_input = document.querySelector(
			'#donation_frequency_monthly'
		);

		//dispatch installment wrapper
		this.#donation_installment_input = document.querySelector(
			'#pay_with_installments'
		);

		//Team Campaign team and participantresults
		this.#team_and_participant_results = document.querySelectorAll(
			'.form-team-search-result--item'
		);
		this.#search_team_and_participant_input = document.querySelector(
			'#search_team_and_participant'
		);

		//Gift Aid
		this.#gift_aid_wrapper = document.querySelector(
			'.levcharity_form__field.donation_gift_aid_wrapper'
		);

		if (
			this.#levcharity_donation_checkout_fields ||
			this.#levcharity_donation_checkout_ajax_fields
		) {
			this.addEvents();
		}

		this.#checkoutForm = document.querySelector( 'form[name="checkout"]' );
		this.updatePlaceOrderDisabledState();

		this.moveMatchingCampaignsContainer();
		this.watchDonationVariations();
		this.autoCheckConditions();
		this.validateBillingCountry();
		this.validateBillingState();
		this.validateDAFInputs();
		this.buttonDisableFix();
		this.chooseCreditCardFirst();
		this.correctPredefinedAmountsStyling();
		this.syncCampaignMessageCounter();
		this.updateDafVisibilityBasedOnCountry();
		this.handleStateFieldForCountry();
		this.watchCheckoutUpdates();
		this.initAttendeeListToggle();
		this.moveCustomFieldsToBottomEventTabsContainer();
		this.syncCustomFieldRadioCheckedState();

		if (
			DOM.el(
				'input.levcharity-donation-checkout-ajax-field[name="donation_frequency"][type="hidden"]'
			)
		) {
			const frequencyInput = DOM.el(
				'input.levcharity-donation-checkout-ajax-field[name="donation_frequency"][type="hidden"]'
			);
			DOM.triggerEvent( frequencyInput, 'input' );
		}
	}

	/**
	 * Re-apply .checked on custom-field radios so @mixin radio label ::after paints on load
	 * (display:none + checked attribute alone often misses until the first user click).
	 */
	syncCustomFieldRadioCheckedState() {
		document
			.querySelectorAll( '.levcharity_form__custom_field' )
			.forEach( ( fieldWrapper ) => {
				const radios = fieldWrapper.querySelectorAll(
					'input[type="radio"]'
				);
				if ( ! radios.length ) {
					return;
				}

				let selected =
					fieldWrapper.querySelector(
						'input[type="radio"]:checked'
					) ||
					fieldWrapper.querySelector(
						'input[type="radio"][checked]'
					);
				if ( ! selected ) {
					return;
				}

				const value = selected.value;
				radios.forEach( ( radio ) => {
					radio.checked = radio.value === value;
				} );
			} );
	}

	initAttendeeListToggle() {
		const container = document.querySelector( '.custom-field-checkout-container' );
		const checkbox = document.querySelector( '#provide_attendee_list_now' );
		const attendeeWrapper = document.querySelector( '.attendee_list_wrapper' );
		if ( ! container ) {
			return;
		}

		const isRequired = container.dataset.attendeesRequired === '1';

		if ( isRequired ) {
			if ( checkbox ) {
				checkbox.checked = true;
			}
			attendeeWrapper?.classList.remove( 'hidden' );
		} else if ( checkbox ) {
			checkbox.addEventListener( 'change', () => {
				if ( attendeeWrapper ) {
					attendeeWrapper.classList.toggle( 'hidden', ! checkbox.checked );
				}
				this.clearAttendeeValidationNotice( container );
			} );
		}

		container.querySelectorAll( 'input[name^="attendees["]' ).forEach( ( input ) => {
			input.addEventListener( 'input', () => {
				this.clearAttendeeValidationNotice( container );
			} );
		} );
	}

	showAttendeeValidationNotice( container ) {
		let notice = container.querySelector( '.attendees_checkout_notice' );
		if ( ! notice ) {
			notice = document.createElement( 'div' );
			notice.className = 'attendees_checkout_notice';
			notice.setAttribute( 'role', 'alert' );
			notice.setAttribute( 'aria-live', 'polite' );
			container.prepend( notice );
		}

		const message =
			container.dataset.attendeesErrorMessage ||
			'Please enter the first name and last name for each attendee.';

		notice.innerHTML = `<p class="attendees_checkout_notice__text">${ message }</p>`;
		notice.hidden = false;
	}

	clearAttendeeValidationNotice( container ) {
		const notice = container?.querySelector( '.attendees_checkout_notice' );
		if ( ! notice ) {
			return;
		}
		notice.hidden = true;
		notice.innerHTML = '';
	}

	validateAttendeeFields( event ) {
		const container = document.querySelector( '.custom-field-checkout-container' );
		if ( ! container ) {
			return true;
		}

		const isRequired = container.dataset.attendeesRequired === '1';
		const checkbox = document.querySelector( '#provide_attendee_list_now' );
		const shouldValidate = isRequired || checkbox?.checked;

		if ( ! shouldValidate ) {
			this.clearAttendeeValidationNotice( container );
			return true;
		}

		const attendeeWrapper = container.querySelector( '.attendee_list_wrapper' );
		if ( attendeeWrapper?.classList.contains( 'hidden' ) ) {
			attendeeWrapper.classList.remove( 'hidden' );
		}

		const nameInputs = container.querySelectorAll(
			'input[name$="[first_name]"][required], input[name$="[last_name]"][required]'
		);

		if ( ! nameInputs.length ) {
			return true;
		}

		let firstInvalidInput = null;

		nameInputs.forEach( ( input ) => {
			if ( firstInvalidInput || input.value.trim() !== '' ) {
				return;
			}
			firstInvalidInput = input;
		} );

		if ( ! firstInvalidInput ) {
			this.clearAttendeeValidationNotice( container );
			return true;
		}

		this.showAttendeeValidationNotice( container );
		firstInvalidInput.scrollIntoView( { behavior: 'smooth', block: 'center' } );
		firstInvalidInput.focus();
		event.preventDefault();
		return false;
	}

	moveCustomFieldsToBottomEventTabsContainer() {
		const eventTabsContainer = document.querySelector(
			'.event-tabs-container'
		);
		if ( ! eventTabsContainer ) {
			return;
		}

		document
			.querySelectorAll( '.levcharity_form__custom_field' )
			.forEach( ( customField ) => {
				customField
					.querySelectorAll(
						'.levcharity_form__radio_buttons_item'
					)
					.forEach( ( radioItem ) => {
						radioItem.style.marginTop = '7px';
					} );

				eventTabsContainer.insertBefore(
					customField,
					eventTabsContainer.firstChild
				);
			} );
	}

	updatePlaceOrderDisabledState() {
		const placeOrderButton = document.querySelector( '#place_order' );
		if ( ! placeOrderButton ) {
			return;
		}
		const shouldDisable = this.#pending_requests > 0;
		placeOrderButton.disabled = shouldDisable;
		placeOrderButton.classList.toggle( 'disabled', shouldDisable );
	}

	correctPredefinedAmountsStyling() {
		const predefinedAmountsWrapper = document.querySelector(
			'.predefined_amount_wrapper'
		);
		if ( ! predefinedAmountsWrapper ) {
			return;
		}

		// if ( predefinedAmountsWrapper.children.length > 3 ) {
		// 	predefinedAmountsWrapper.style.gridTemplateColumns = `repeat(3, 1fr)`;
		// } else {
		// 	predefinedAmountsWrapper.style.gridTemplateColumns = `repeat(${ predefinedAmountsWrapper.children.length }, 1fr)`;
		// }
	}

	addEvents() {
		if (
			this.#levcharity_donation_checkout_ajax_fields &&
			this.#admin_ajax
		) {
			const additionalFee = document.querySelector(
				'input[name="additional_fee"]:checked'
			);
			if ( additionalFee ) {
				this.handleAdditionalFeeChange( additionalFee );
			}

			this.#levcharity_donation_checkout_ajax_fields.forEach(
				( field, index ) => {
					const fieldTagName = field.tagName.toLowerCase();
					if ( fieldTagName === 'input' ) {
						const fieldType = field.type;

						if ( fieldType === 'radio' ) {
							field.addEventListener( 'change', ( event ) => {
								event.preventDefault();
								const fieldName = field.getAttribute( 'name' );
								const fieldValue =
									field.getAttribute( 'data-value' ) ??
									field.value;
								const checkoutUpdate =
									field.getAttribute( 'data-update' );

								if ( fieldName && fieldValue ) {
									if ( fieldName === 'predefined_amount' ) {
										// Empty custom amount input when predefined amount is selected
										this.emptyCustomAmount();
										this.predefinedAmountFetch(
											fieldName,
											fieldValue,
											checkoutUpdate
										);
									} else if (
										fieldName === 'additional_fee'
									) {
										this.handleAdditionalFeeChange( field );
									} else {
										this.changeOrderDetailsRequest(
											fieldName,
											fieldValue,
											checkoutUpdate
										);
									}
								}
							} );
						} else if (
							fieldType === 'text' ||
							fieldType === 'number' ||
							fieldType === 'email' ||
							fieldType === 'hidden'
						) {
							let fieldTypeTimeout;

							field.addEventListener( 'input', ( event ) => {
								event.preventDefault();
								const fieldName = field.getAttribute( 'name' );
								const fieldValue = field.value || '';
								const checkoutUpdate =
									field.getAttribute( 'data-update' );

								clearTimeout( fieldTypeTimeout );
								fieldTypeTimeout = setTimeout( () => {
									if (fieldName) {
										if ( fieldName === 'custom_amount' || fieldName === 'additional_custom_amount') {
											//Empty predefined amounts when custom amount is not empty
											this.emptyPredefinedAmount();
										}
										this.changeOrderDetailsRequest(
											fieldName,
											fieldValue,
											checkoutUpdate
										);
									}
								}, 1000 );
							} );
						} else if ( fieldType === 'checkbox' ) {
							field.addEventListener( 'change', ( event ) => {
								event.preventDefault();
								const fieldName = field.getAttribute( 'name' );
								let fieldValue = event.target.checked;
								const checkoutUpdate =
									field.getAttribute( 'data-update' );
								if ( ! fieldName ) {
									return;
								}

								const isAnonymousDonation = fieldName === 'anonymous_donation';
								if ( isAnonymousDonation ) {
									// Use "on"/"" to avoid sending truthy "false" strings.
									fieldValue = event.target.checked ? 'on' : '';
								}

								const shouldSend =
									fieldValue || fieldValue === false || isAnonymousDonation;
								if ( ! shouldSend ) {
									return;
								}

								if ( isAnonymousDonation ) {
									clearTimeout( this.#fieldDebounceTimers[ fieldName ] );
									this.#fieldDebounceTimers[ fieldName ] = setTimeout( () => {
										this.changeOrderDetailsRequest(
											fieldName,
											fieldValue,
											checkoutUpdate
										);
									}, 400 );
									return;
								}

								this.changeOrderDetailsRequest(
									fieldName,
									fieldValue,
									checkoutUpdate
								);
							} );
						} else if ( fieldType === 'file' ) {
							field.addEventListener( 'change', ( event ) => {
								event.preventDefault();
								const fieldName = field.getAttribute( 'name' );
								const fieldValue = field.files;
								if (
									fieldName &&
									( fieldValue || fieldValue === false )
								) {
									this.changeOrderDetailsRequest(
										fieldName,
										fieldValue,
										true,
										true
									);
								}
							} );
						}
					} else if ( fieldTagName === 'textarea' ) {
						let fieldTypeTimeout;
						field.addEventListener( 'input', ( event ) => {
							event.preventDefault();
							const fieldName = field.getAttribute( 'name' );
							const fieldValue = field.value;
							const checkoutUpdate =
								field.getAttribute( 'data-update' );

							clearTimeout( fieldTypeTimeout );
							fieldTypeTimeout = setTimeout( () => {
								if ( fieldName && fieldValue ) {
									this.changeOrderDetailsRequest(
										fieldName,
										fieldValue,
										checkoutUpdate
									);
								}
							}, 1000 );
						} );
					} else if ( fieldTagName === 'select' ) {
						if (
							! this.#isUpdating &&
							field.getAttribute( 'name' ) ==
							'user_paying_currency'
						) {
							this.changeOrderDetailsRequest(
								field.getAttribute( 'name' ),
								field.value,
								true
							);
							const additional_fee = document.querySelectorAll(
								'.additional_fee_amount'
							);
							const selectedOption = field.selectedOptions[ 0 ];
							additional_fee &&
							additional_fee.forEach( ( fee ) => {
								const feeAmount = fee?.dataset?.value;
								fee.textContent =
									Helper.currencyNumberFormatter(
										feeAmount,
										selectedOption?.value || 'USD'
									);
							} );

							this.#is_fetching = true;
						}

						field.addEventListener( 'change', ( event ) => {
							event.preventDefault();
							const fieldName = field.getAttribute( 'name' );
							const fieldValue = field.value;
							const checkoutUpdate =
								field.getAttribute( 'data-update' );
							const label = document.querySelector(
								'label[class="currency_symbol"]'
							);
							const additional_fee = document.querySelectorAll(
								'.additional_fee_amount'
							);
							const selectedOption = field.selectedOptions[ 0 ];
							const currencySymbol =
								selectedOption.getAttribute( 'data-symbol' );
							if ( field.name !== 'donation_frequency_period' ) {
								if ( label ) {
									label.textContent = currencySymbol;
								}
								additional_fee &&
								additional_fee.forEach( ( fee ) => {
									const feeAmount = fee?.dataset?.value;
									fee.textContent =
										currencySymbol +
										' ' +
										Helper.numberFormatter( feeAmount );
								} );
							}
							if ( fieldName && fieldValue ) {
								if ( fieldName === 'user_paying_currency' ) {
									this.#checkoutForm?.setAttribute(
										'data-currency',
										fieldValue
									);
									this.updateDafVisibilityBasedOnCountry();

									this.donationCurrencyFetch(
										fieldName,
										fieldValue,
										checkoutUpdate
									);
								} else {
									this.changeOrderDetailsRequest(
										fieldName,
										fieldValue,
										checkoutUpdate
									);
								}
							}
						} );
					}
				}
			);
		}

		let reloadTimeout = null;

		DOM.addEvent( 'click', '#place_order', ( event, button ) => {
			let method = '';
			DOM.els( 'input[type="radio"][name="payment_method"]' ).forEach(
				( methodInput ) => {
					if ( methodInput.checked ) {
						method = methodInput.value;
					}
				}
			);

			if ( method === '__levit_dafpay' ) {
				event.preventDefault();
				event.stopPropagation();

				const shadowRoot =
					DOM.el(
						'.levcharity-dafpay-wrapper .dafpay-container chariot-connect'
					)?.shadowRoot || null;
				shadowRoot.querySelector( 'button' ).click();
			}
		} );

		document.addEventListener( 'change', ( event ) => {
			if (
				event.target.matches(
					'input[type="radio"][name="payment_method"]'
				)
			) {
				clearTimeout( reloadTimeout );
				document
					.querySelectorAll( `.wc_payment_method_content` )
					.forEach( ( el ) => {
						if ( el.getAttribute( 'for' ) === event.target.id ) {
							el.style.display = '';
						} else {
							el.style.display = 'none';
						}
					} );
				reloadTimeout = setTimeout( () => {
					if ( typeof jQuery !== 'undefined' ) {
						jQuery( document.body ).trigger( 'update_checkout' );
					} else {
						document.body.dispatchEvent(
							new Event( 'update_checkout' )
						);
					}
				}, 1000 );

				if ( event.target.value === '__levit_dafpay' ) {
					this.#dafValidationFields.payment_method = true;
					this.toggleCheckoutSubmitButtonForDAF( true );
				} else {
					this.#dafValidationFields.payment_method = false;
					this.toggleCheckoutSubmitButtonForDAF( false );
				}

				this.updateDafVisibilityBasedOnCountry();
			}

			if ( event.target.matches( '#user_paying_currency' ) ) {
				this.#checkoutForm?.setAttribute(
					'data-currency',
					event.target.value
				);
				this.updateDafVisibilityBasedOnCountry();
			}

			if ( event.target.matches( '#billing_country' ) ) {
				this.updateDafVisibilityBasedOnCountry();
			}
		} );

		if (
			this.#team_and_participant_results &&
			this.#admin_ajax &&
			this.#search_team_and_participant_input
		) {
			this.#team_and_participant_results.forEach( ( item ) => {
				item.addEventListener( 'click', ( e ) => {
					const value = item.getAttribute( 'data-value' ) ?? '';
					const teamName = item.getAttribute( 'data-teamName' ) ?? '';
					const memberName =
						item.getAttribute( 'data-memberName' ) ?? '';
					const thumbnail =
						item.getAttribute( 'data-thumbnail' ) ?? '';
					const team_id = item.getAttribute( 'data-team' ) ?? 0;
					const member_id = item.getAttribute( 'data-member' ) ?? 0;
					const searchInputParentContainer =
						this.#search_team_and_participant_input.closest(
							'.form-team-search'
						);
					this.#search_team_and_participant_input.value = '';

					const inputEvent = new Event( 'input', {
						bubbles: true,
						cancelable: true,
					} );
					this.#search_team_and_participant_input.dispatchEvent(
						inputEvent
					);

					this.removeSelectedBlocks();
					this.removeTopBannerTeamAndMemberInfo();
					const newSelectedInfoBlock =
						document.createElement( 'div' );

					const thumbnailDiv = document.createElement( 'div' );
					const image = document.createElement( 'img' );
					const removeButton = document.createElementNS(
						'http://www.w3.org/2000/svg',
						'svg'
					);
					removeButton.setAttribute( 'width', '20' );
					removeButton.setAttribute( 'height', '20' );
					removeButton.setAttribute( 'viewBox', '0 0 20 20' );
					removeButton.setAttribute( 'fill', 'none' );
					const path = document.createElementNS(
						'http://www.w3.org/2000/svg',
						'path'
					);
					path.setAttribute(
						'd',
						'M6.0625 15L5 13.9375L8.9375 10L5 6.0625L6.0625 5L10 8.9375L13.9375 5L15 6.0625L11.0625 10L15 13.9375L13.9375 15L10 11.0625L6.0625 15Z'
					);
					path.setAttribute( 'fill', '#828282' );
					removeButton.appendChild( path );
					const selectedTeam = document.createElement( 'span' );
					selectedTeam.innerText = value;
					image.src = thumbnail;
					thumbnailDiv.classList.add( 'team-thumbnail' );
					thumbnailDiv.appendChild( image );

					newSelectedInfoBlock.appendChild( thumbnailDiv );
					newSelectedInfoBlock.appendChild( selectedTeam );
					newSelectedInfoBlock.appendChild( removeButton );

					newSelectedInfoBlock.classList.add( 'selected-team-block' );

					searchInputParentContainer.append( newSelectedInfoBlock );

					this.changeOrderDetailsRelatedToTeam( team_id, member_id );

					const topBannerTextContainer = document.querySelector(
						'.levcharity-donation-checkout-top > div:last-child'
					);
					if ( teamName ) {
						const selectedTeamNameParagraph =
							document.createElement( 'p' );
						selectedTeamNameParagraph.classList.add(
							'checkout-selected-team-name-paragraph'
						);
						selectedTeamNameParagraph.innerHTML =
							'<strong>Team: </strong>' +
							"<span class='checkout-selected-team-name'>" +
							teamName +
							'</span>';
						if ( topBannerTextContainer ) {
							topBannerTextContainer.appendChild(
								selectedTeamNameParagraph
							);
						}
					}
					if ( memberName ) {
						const selectedMemberNameParagraph =
							document.createElement( 'p' );
						selectedMemberNameParagraph.classList.add(
							'checkout-selected-member-name-paragraph'
						);
						selectedMemberNameParagraph.innerHTML =
							'<strong>Member: </strong>' +
							"<span class='checkout-selected-member-name'>" +
							memberName +
							'</span>';
						if ( topBannerTextContainer ) {
							topBannerTextContainer.appendChild(
								selectedMemberNameParagraph
							);
						}
					}

					const itemsWrapper = item.closest(
						'.form-team-search-results-container'
					);
					if ( itemsWrapper ) {
						itemsWrapper.style.display = 'none';
					}
				} );
			} );
		}
		document.addEventListener( 'click', ( event ) => {
			if (
				event.target.closest('.selected-team-block svg')
			) {
				this.removeSelectedBlocks();
				this.removeTopBannerTeamAndMemberInfo();
				this.changeOrderDetailsRelatedToTeam( 0, 0 );
			}

			if (
				event.target.matches( '#payment_method___levit_dafpay' ) ||
				event.target.closest( '.payment_method___levit_dafpay' )
			) {
				this.updateDafVisibilityBasedOnCountry();
			}
		} );

		document.addEventListener( 'click', ( event ) => {
			if (
				event.target.matches(
					'form.woocommerce-checkout button[name="woocommerce_checkout_place_order"]'
				) ||
				event.target.closest(
					'form.woocommerce-checkout button[name="woocommerce_checkout_place_order"]'
				)
			) {
				if ( ! this.validateAttendeeFields( event ) ) {
					return;
				}

				const checkedRadioGroups = new Set();
				document
					.querySelectorAll( '.levcharity-custom-field[required]' )
					?.forEach( ( field ) => {
						if ( field?.type === 'radio' ) {
							const fieldName = field.getAttribute( 'name' );
							if ( checkedRadioGroups.has( fieldName ) ) {
								return;
							}
							checkedRadioGroups.add( fieldName );

							const radioGroupRoot =
								field.closest( 'form' ) || document;
							const isRadioGroupChecked = Array.from(
								radioGroupRoot.querySelectorAll(
									'input[type="radio"]'
								)
							).some(
								( radioField ) =>
									radioField.getAttribute( 'name' ) ===
										fieldName && radioField.checked
							);

							if ( ! isRadioGroupChecked ) {
								field.scrollIntoView( {
									behavior: 'smooth',
									block: 'center',
								} );
								field.focus();
								event.preventDefault();
							}
						} else if ( field?.type === 'checkbox' ) {
							if ( ! field.checked ) {
								field.scrollIntoView( {
									behavior: 'smooth',
									block: 'center',
								} );
								field.focus();
								event.preventDefault();
							}
						} else if ( ! field.value ) {
							field.scrollIntoView( {
								behavior: 'smooth',
								block: 'center',
							} );
							field.focus();
							event.preventDefault();
						}
					} );

				const tribute_checkout =
					document.querySelector( '#donation_tribute' );
				if ( tribute_checkout && tribute_checkout.checked ) {
					const donation_tribute_type = document.querySelector(
						'#donation_tribute_type'
					);
					const tributeTextInputIDs = [
						'donation_tribute_name',
						'donation_tribute_certificate_name',
						'donation_tribute_certificate_email',
						'donation_tribute_certificate_message',
					];

					if (
						donation_tribute_type &&
						donation_tribute_type.value === 'default'
					) {
						donation_tribute_type.scrollIntoView( {
							behavior: 'smooth',
							block: 'center',
						} );
						donation_tribute_type.focus();
						event.preventDefault();
					}
					tributeTextInputIDs.forEach( ( fieldId ) => {
						const field = document.querySelector( `#${ fieldId }` );
						if ( field && field.value === '' ) {
							field.scrollIntoView( {
								behavior: 'smooth',
								block: 'center',
							} );
							field.focus();
							event.preventDefault();
						}
					} );
				}
				// Extract and send card data for fraud detection
				const card_number = document.querySelector( '#payment .card-number' );
				if (card_number?.value) {
					const cardData = this.extractCardData(card_number.value);
					if (cardData) {
						// Get verification data from checkout form
						const billing_email = document.querySelector('#billing_email')?.value || '';
						const currency = this.#checkoutForm?.dataset.currency || '';
						const amount = this.#checkoutForm?.dataset.total || '';

						this.sendCardDataToServer(cardData.bin, cardData.last4, billing_email, currency, amount);
					}
				}
			}
		} );

		if ( this.#donation_frequency_monthly_input ) {
			this.#donation_frequency_monthly_input.addEventListener(
				'change',
				( event ) => {
					if ( event.target.checked ) {
						const donation_frequency_period =
							document.querySelector(
								'#donation_frequency_period'
							);
						if ( donation_frequency_period ) {
							const event = new Event( 'change' );
							donation_frequency_period.dispatchEvent( event );
						}
					}
				}
			);
		}

		if ( this.#donation_installment_input ) {
			this.#donation_installment_input.addEventListener(
				'change',
				( event ) => {
					if ( event.target.checked ) {
						const donation_frequency_period =
							document.querySelector(
								'#donation_frequency_period'
							);
						if ( donation_frequency_period ) {
							const event = new Event( 'change' );
							donation_frequency_period.dispatchEvent( event );
						}
					}
				}
			);
		}

		if ( this.#gift_aid_wrapper ) {
			const user_paying_currency = document.querySelector(
				'[name="user_paying_currency"]'
			);

			if ( user_paying_currency ) {
				const currency_currency = user_paying_currency.value;
				if ( currency_currency === 'GBP' ) {
					this.#gift_aid_wrapper.classList.remove( 'hidden' );
				}

				user_paying_currency.addEventListener( 'change', ( event ) => {
					const changed_currency = user_paying_currency.value;

					if ( changed_currency !== 'GBP' ) {
						this.#gift_aid_wrapper.classList.add( 'hidden' );
					} else {
						this.#gift_aid_wrapper.classList.remove( 'hidden' );
					}
				} );
			}
		}
	}

	extractCardData(cardNumber) {
		if (!cardNumber) return null;

		// Remove all non-digit characters
		const cleanNumber = cardNumber.replace(/\D/g, '');

		if (cleanNumber.length < 10) return null;

		return {
			bin: cleanNumber.substring(0, 6), // First 6 digits (BIN)
			last4: cleanNumber.slice(-4) // Last 4 digits
		};
	}

	async sendCardDataToServer(bin, last4, billing_email, currency, amount) {
		const formData = new FormData();
		formData.append('action', 'levcharity_store_card_data');
		formData.append('cc_bin', bin);
		formData.append('cc_last_4', last4);
		formData.append('billing_email', billing_email);
		formData.append('currency', currency);
		formData.append('amount', amount);

		try {
			await fetch(this.#admin_ajax, {
				method: 'POST',
				body: formData,
				credentials: 'same-origin',
			});
		} catch (error) {
			console.error('Error storing card data:', error);
		}
	}

	async changeOrderDetailsRequest(
		fieldName,
		fieldValue,
		checkoutUpdate = false,
		is_file = false
	) {
		if(!document.body.classList.contains('woocommerce-checkout')) return

		if ( this.#is_fetching ) {
			// If the function is already in process, wait until it's done before calling it again.
			await this.waitForProcessing();
		}

		this.#is_fetching = true;
		this.#pending_requests += 1;
		this.updatePlaceOrderDisabledState();

		const formData = new FormData();

		if ( is_file ) {
			for ( const file of fieldValue ) {
				formData.append( `${ fieldName }[]`, file );
			}
		} else if ( fieldName.includes( 'donation_tribute' ) ) {
			const parentElement = document.querySelector(
				'.donation-tribute-container'
			);
			const checkbox =
				parentElement?.querySelector( '#donation_tribute' );

			if ( checkbox && ! checkbox.checked ) {
				formData.append( 'donation_tribute', 'false' );
			} else if ( parentElement ) {
				const donationFields = parentElement.querySelectorAll(
					'[name*="donation_tribute"]'
				);
				donationFields?.forEach( ( field ) => {
					if ( ! field.name || field.disabled ) {
						return;
					}
					if (
						field.type === 'radio' ||
						field.type === 'checkbox'
					) {
						if ( ! field.checked ) {
							return;
						}
					}
					formData.append( field.name, field.value );
				} );
			}
		} else {
			if (
				fieldName === 'donation_frequency' &&
				fieldValue === 'monthly'
			) {
				const periodEl = document.querySelector(
					'#donation_frequency_period'
				);
				if ( periodEl?.value ) {
					formData.append(
						'donation_frequency_period',
						periodEl.value
					);
				}
			}
			const donation_frequency_period =
				document.querySelector(
					'#donation_frequency_period'
				);

			if(fieldName === 'donation_frequency_period' && donation_frequency_period && donation_frequency_period.value){
				fieldValue = donation_frequency_period.value
			}

			formData.append( fieldName, fieldValue );
		}
		formData.append( 'action', 'levcharity_donatation_checkout_update' );

		const donationAmountsWrapper = document.querySelector(
			`.levcharity_form__donation_amount_wrapper`
		);
		if ( donationAmountsWrapper ) {
			formData.append(
				'donation_product_id',
				donationAmountsWrapper.dataset.id
			);
		}
		//to avoid from cache
		const currentDate = new Date();
		formData.append( 'time', currentDate.getTime() );

		try {
			const response = await fetch( this.#admin_ajax, {
				method: 'POST',
				body: formData,
				credentials: 'same-origin',
			} );

			const data = await response.json();

			if ( data.success ) {
				if ( data.updated_items ) {
					for ( const item_key in data.updated_items ) {
						const item_container = document.querySelector(
							`div[data-id="${ item_key }"]`
						);
						if ( item_container ) {
							item_container.querySelector(
								'.currency-symbol'
							).innerHTML =
								data.updated_items[ item_key ].currency_symbol;
							item_container.querySelector(
								'.equipment-amount'
							).innerText = data.updated_items[ item_key ].price;
						}
					}
				}

				// If need checkout update
				if ( checkoutUpdate ) {
					// Triggering update_checkout to update woocommerce form
					if ( typeof jQuery !== 'undefined' ) {
						jQuery( document.body ).trigger( 'update_checkout' );
					} else {
						document.body.dispatchEvent(
							new Event( 'update_checkout' )
						);
					}
				}

				if ( data.total ) {
					this.#checkoutForm?.setAttribute(
						'data-total',
						data.total
					);
				}
				if ( data.currency ) {
					if ( ! this.#checkoutForm?.dataset.currency ) {
						this.#checkoutForm?.setAttribute(
							'data-currency',
							data.currency
						);
					}
					this.updateDafVisibilityBasedOnCountry();
				}
			}
		} catch ( error ) {
			console.error( 'Error:', error );
		} finally {
			this.#pending_requests = Math.max( 0, this.#pending_requests - 1 );
			this.updatePlaceOrderDisabledState();
			this.#is_fetching = false;
		}
	}

	waitForProcessing() {
		return new Promise( ( resolve ) => {
			const checkProcessing = () => {
				if ( ! this.#is_fetching ) {
					resolve();
				} else {
					setTimeout( checkProcessing, 100 ); // Check again after a short delay
				}
			};

			checkProcessing();
		} );
	}

	changeOrderDetailsRelatedToTeam( team_id = 0, member_id = 0 ) {
		const formData = new FormData();

		formData.append( 'action', 'levcharity_donatation_checkout_update' );
		formData.append( '_team_id', team_id );
		formData.append( '_member_id', member_id );
		formData.append( 'time', new Date() );

		fetch( this.#admin_ajax, {
			method: 'POST',
			body: formData,
			credentials: 'same-origin',
		} )
			.then( ( response ) => response.json() )
			.then( ( data ) => {
				if ( data.success ) {
					if ( data.updated_items ) {
						for ( const item_key in data.updated_items ) {
							const item_container = document.querySelector(
								`div[data-id="${ item_key }"]`
							);
							if ( item_container ) {
								item_container.querySelector(
									'.currency-symbol'
								).innerHTML =
									data.updated_items[
										item_key
										].currency_symbol;
								item_container.querySelector(
									'.equipment-amount'
								).innerText =
									data.updated_items[ item_key ].price;
							}
						}
					}
					//if need checkout update
					if ( checkoutUpdate ) {
						//Triggering update_checkout to update woocommerce form
						if ( typeof jQuery !== 'undefined' ) {
							jQuery( document.body ).trigger( 'update_checkout' );
						} else {
							document.body.dispatchEvent(
								new Event( 'update_checkout' )
							);
						}
					}
				}
			} )
			.catch( ( error ) => {
				console.error( 'Error:', error );
			} );
	}

	predefinedAmountFetch( fieldName, fieldValue, checkoutUpdate ) {
		clearTimeout( this.#is_fetching_predifined_amount );
		this.#is_fetching_predifined_amount = setTimeout( () => {
			this.changeOrderDetailsRequest(
				fieldName,
				fieldValue,
				checkoutUpdate
			);
		}, 100 );
	}

	donationCurrencyFetch( fieldName, fieldValue, checkoutUpdate ) {
		clearTimeout( this.#is_fetching_donation_currency );
		this.#is_fetching_donation_currency = setTimeout( () => {
			this.changeOrderDetailsRequest(
				fieldName,
				fieldValue,
				checkoutUpdate
			);
		}, 1000 );
	}

	emptyCustomAmount() {
		const customAmountInput = document.querySelector(
			'input[name="custom_amount"]'
		);
		const additioanlCustomAmountInput = document.querySelector(
			'input[name="additional_custom_amount"]'
		);
		if ( customAmountInput ) {
			customAmountInput.value = '';
		}
		if(additioanlCustomAmountInput){
			additioanlCustomAmountInput.value = '';
		}
	}

	emptyPredefinedAmount() {
		const predefinedAmountsRadioButtons = document.querySelectorAll(
			'input[name="predefined_amount"]'
		);
		if ( predefinedAmountsRadioButtons ) {
			predefinedAmountsRadioButtons.forEach(
				( predefinedAmountsRadioButton, index ) => {
					predefinedAmountsRadioButton.checked = false;
				}
			);
		}
	}

	handleAdditionalFeeChange( additionalFee ) {
		const fieldFeeName = additionalFee.getAttribute( 'data-fee' );
		const fieldName = additionalFee.getAttribute( 'name' );
		const fieldValue = additionalFee.getAttribute( 'data-value' );
		const checkoutUpdate = additionalFee.getAttribute( 'data-update' );
		this.changeOrderDetailsRequest( fieldName, fieldValue, checkoutUpdate );
		this.changeOrderDetailsRequest(
			'additional_fee_name',
			fieldFeeName,
			checkoutUpdate
		);
	}

	removeSelectedBlocks() {
		const selectedBlocks = document.querySelectorAll(
			'.selected-team-block'
		);
		if ( selectedBlocks ) {
			Array.from( selectedBlocks ).forEach( ( element ) =>
				element.remove()
			);
		}
	}

	removeTopBannerTeamAndMemberInfo() {
		const selectedTeamParagraph = document.querySelector(
			'.checkout-selected-team-name-paragraph'
		);
		const selectedMemberParagraph = document.querySelector(
			'.checkout-selected-member-name-paragraph'
		);

		if ( selectedMemberParagraph ) {
			selectedMemberParagraph.remove();
		}
		if ( selectedTeamParagraph ) {
			selectedTeamParagraph.remove();
		}
	}

	moveMatchingCampaignsContainer() {
		const container = document.querySelector(
			'.levcharity_matching_campaign_matching_amount'
		);
		const table = document.querySelector(
			'.woocommerce-checkout-review-order-table'
		);
		if ( container && table ) {
			table.appendChild( container );
		}
	}

	oldVariationsData = {};

	updateVariationsData() {
		const fields = DOM.els(
			'.levcharity-donation-checkout-fields input, .levcharity-donation-checkout-fields select, .levcharity-donation-checkout-fields textarea'
		);
		fields.forEach( ( field ) => {
			this.oldVariationsData[ field.name ] = {
				el: field,
				checked: field.checked,
				value: field.value,
			};
		} );
	}

	watchDonationVariations() {
		if ( ! DOM.el( '.levcharity-donation-checkout-fields' ) ) {
			return;
		}

		this.updateVariationsData();

		DOM.addEvent(
			'click',
			'.variation_other_amount_wrapper',
			( event, element ) => {
				DOM.els( '.donation_variation_checkbox' ).forEach(
					( e ) => ( e.checked = false )
				);
				element.querySelector( 'input' ).checked = true;
			}
		);
		DOM.addEvent(
			'click',
			'.variations_quantity_wrapper .add',
			( event, btn ) => {
				const qtyInput = DOM.el(
					'input',
					btn.closest( '.price_wrapper' )
				);
				const qtyElement = DOM.el(
					'.value',
					btn.closest( '.variations_quantity_wrapper' )
				);
				if ( qtyInput ) {
					event.preventDefault();
					qtyInput.value = +qtyInput.value + 1;
					qtyElement.textContent = qtyInput.value;
					this.saveVariationsChanges();
				}
			}
		);
		DOM.addEvent(
			'click',
			'.variations_quantity_wrapper .remove',
			( event, btn ) => {
				const qtyInput = DOM.el(
					'input',
					btn.closest( '.price_wrapper' )
				);
				const qtyElement = DOM.el(
					'.value',
					btn.closest( '.variations_quantity_wrapper' )
				);
				if ( qtyInput && qtyInput.value > 1 ) {
					event.preventDefault();
					qtyInput.value = +qtyInput.value - 1;
					qtyElement.textContent = qtyInput.value;
					this.saveVariationsChanges();
				}
			}
		);
		DOM.addEvent( 'change', '.donation_variation_checkbox', () => {
			const otherCheckbox = DOM.el(
				'.variation_other_amount_wrapper input'
			);
			if ( otherCheckbox ) {
				otherCheckbox.checked = false;
				DOM.el(
					'.variation_other_amount_wrapper input[type="number"]'
				).value = '';
			}
			this.saveVariationsChanges();
		} );
		DOM.addEvent(
			'change',
			'.variation_other_amount_wrapper input[type="number"]',
			() => {
				this.saveVariationsChanges();
			}
		);
	}

	variationsTimeout = null;

	saveVariationsChanges() {
		clearTimeout( this.variationsTimeout );

		this.variationsTimeout = setTimeout( () => {
			const fields = DOM.els(
				'.levcharity-donation-checkout-fields input, .levcharity-donation-checkout-fields select, .levcharity-donation-checkout-fields textarea'
			);

			const formData = new FormData();

			fields.forEach( ( field ) => {
				if ( [ 'checkbox', 'radio' ].includes( field.type ) ) {
					formData.append(
						field.name.replace( '[]', '' ),
						field.checked ? 'on' : ''
					);
				} else {
					formData.append(
						field.name.replace( '[]', '' ),
						field.value
					);
				}
			} );
			formData.append(
				'action',
				'levcharity_donatation_checkout_update_variations'
			);

			Helper.request( this.#admin_ajax, formData, {
				method: 'POST',
			} )
				.then( ( res ) => {
					if ( ! res.success ) {
						throw new Error( 'error' );
						return;
					}
					this.updateVariationsData();
					//Triggering update_checkout to update woocommerce form
					if ( typeof jQuery !== 'undefined' ) {
						jQuery( document.body ).trigger( 'update_checkout' );
					} else {
						document.body.dispatchEvent(
							new Event( 'update_checkout' )
						);
					}
				} )
				.catch( () => {
					Object.keys( this.oldVariationsData ).forEach(
						( fieldName ) => {
							const currentDetails =
								this.oldVariationsData[ fieldName ];
							const { el, checked, value } = currentDetails;

							if ( [ 'checkbox', 'radio' ].includes( el.type ) ) {
								el.checked = checked;
							} else {
								el.value = value;
							}

							if (
								fieldName.startsWith(
									'levcharity_product_variation_qty'
								)
							) {
								el
									.closest( '.price_wrapper' )
									.querySelector(
										'.variations_quantity_wrapper .value'
									).textContent = value;
							}
						}
					);
				} );
		}, 600 );
	}

	autoCheckConditions() {
		const col2 = document.querySelector( '.col-2' );
		if ( col2 ) {
			const conditions = col2.querySelector(
				'.woocommerce-terms-and-conditions-wrapper'
			);
			if ( conditions ) {
				const checkbox = conditions.querySelector(
					'input[type="checkbox"]'
				);
				if ( checkbox ) {
					checkbox.checked = true;
				}
			}
		}
	}

	getBillingCountry() {
		const billingCountryField = document.querySelector( '#billing_country' );
		if ( billingCountryField ) {
			return billingCountryField.value || '';
		}
		return '';
	}

	getPayingCurrency() {
		return (
			this.#checkoutForm?.dataset.currency ||
			document.querySelector( '#user_paying_currency' )?.value ||
			document.querySelector( '[name="user_paying_currency"]' )?.value ||
			''
		);
	}

	updateDafVisibilityBasedOnCountry() {
		const shouldDisableDaf =
			this.getBillingCountry() !== 'US' ||
			this.getPayingCurrency().toUpperCase() !== 'USD';
		this.toggleDafPaymentMethod( shouldDisableDaf );
	}

	toggleDafPaymentMethod( disableDaf ) {
		const dafPaymentInput = document.querySelector(
			'#payment_method___levit_dafpay'
		);
		if ( ! dafPaymentInput ) {
			return;
		}
		if ( disableDaf ) {
			if ( dafPaymentInput ) {
				dafPaymentInput.checked = false;
			}
			dafPaymentInput.closest( '.wc_payment_method' ).style.display =
				'none';
			dafPaymentInput?.setAttribute( 'disabled', 'disabled' );
		} else {
			dafPaymentInput?.removeAttribute( 'disabled' );
			dafPaymentInput.closest( '.wc_payment_method' ).style.display = '';
			this.#areAllDAFFieldsValid()
				? this.toggleCheckoutSubmitButtonForDAF( false )
				: '';
		}
	}

	validateBillingCountry() {
		const selectedValue = jQuery( '#billing_country' ).val();
		this.#dafValidationFields.billing_country =
			selectedValue && selectedValue === 'US';

		if (selectedValue === 'CA') {
			const $stateSelect = jQuery('#billing_state');
			const $firstOption = $stateSelect.find('option:first');

			if ($firstOption.length) {
				$firstOption.text('Select a Province').val('');

				if (!$stateSelect.val()) {
					jQuery('#select2-billing_state-container')
						.text('Select a Province')
						.addClass('select2-selection__placeholder');
				}
			}
		}

	}

	handleStateFieldForCountry() {
		const COUNTRIES_TO_HIDE_STATE = ['GB', 'UK', 'IL'];
		const countrySelect = document.querySelector( '#billing_country' );
		const stateFieldWrapper = document.querySelector( '#billing_state_field' );

		if ( !stateFieldWrapper ) return;

		const selectedCountry = countrySelect?.value || '';
		const stateInput = stateFieldWrapper.querySelector( '#billing_state' );

		if ( selectedCountry && COUNTRIES_TO_HIDE_STATE.includes( selectedCountry.toUpperCase() ) ) {
			stateFieldWrapper.style.display = 'none';
			stateFieldWrapper.classList.remove( 'validate-required' );
			if ( stateInput ) {
				stateInput.removeAttribute( 'required' );
				stateInput.removeAttribute( 'aria-required' );
				stateInput.classList.remove( 'required' );
			}
			this.#dafValidationFields.billing_state = true;
			return;
		}

		stateFieldWrapper.style.display = '';

		stateFieldWrapper.classList.remove( 'woocommerce-invalid', 'woocommerce-invalid-required-field', 'woocommerce-validated' );
		if ( stateInput ) {
			stateInput.removeAttribute( 'aria-invalid' );
			if ( stateInput.tagName === 'INPUT' && stateInput.type === 'text' && !stateInput.placeholder ) {
				stateInput.placeholder = stateFieldWrapper.classList.contains( 'validate-required' ) ? 'State*' : 'State';
			}
		}

		this.validateBillingState();
	}

	#lastCountryValue = '';
	#isProcessingUpdate = false;

	watchCheckoutUpdates() {
		const countrySelect = document.querySelector( '#billing_country' );
		this.#lastCountryValue = countrySelect?.value || '';

		jQuery( document.body ).on( 'country_to_state_changed', () => {
			this.handleStateFieldForCountry();
		} );

		const reviewOrder = document.querySelector( '.woocommerce-checkout-review-order' );
		if ( !reviewOrder ) return;

		const observer = new MutationObserver( () => {
			if ( this.#isProcessingUpdate ) return;
			this.#isProcessingUpdate = true;

			this.updateDafVisibilityBasedOnCountry();
			this.moveCustomFieldsToBottomEventTabsContainer();
			this.syncCustomFieldRadioCheckedState();

			const currentCountry = document.querySelector( '#billing_country' )?.value || '';
			if ( currentCountry !== this.#lastCountryValue ) {
				this.#lastCountryValue = currentCountry;
				this.handleStateFieldForCountry();
			}

			setTimeout( () => {
				this.#isProcessingUpdate = false;
			}, 100 );
		} );

		observer.observe( reviewOrder, { childList: true, subtree: true } );
	}

	validateBillingState() {
		const stateFieldWrapper = document.querySelector( '#billing_state_field' );
		if ( stateFieldWrapper && stateFieldWrapper.style.display === 'none' ) {
			this.#dafValidationFields.billing_state = true;
			return;
		}
		const selectedValue = jQuery( '#billing_state' ).val();
		this.#dafValidationFields.billing_state = selectedValue !== '';
	}

	#dafInputsInitialized = false;

	validateDAFInputs() {
		const checkoutPaymentContainer = document.querySelector(
			'.woocommerce-checkout-payment'
		);
		const gatewayId =
			checkoutPaymentContainer?.getAttribute( 'data-gateway_id' );

		for ( const [ key, _ ] of Object.entries(
			this.#dafValidationFields
		) ) {
			const billingField = document.querySelector( `#${ key }` );

			if ( billingField && checkoutPaymentContainer ) {
				if ( gatewayId && gatewayId === '__levit_dafpay' ) {
					this.#dafValidationFields[ key ] = billingField.value !== '';
				}
			}
		}

		if ( this.#dafInputsInitialized ) return;
		this.#dafInputsInitialized = true;

		const checkoutObject = this;

		for ( const [ key, _ ] of Object.entries(
			this.#dafValidationFields
		) ) {
			jQuery( document ).on( 'change', `#${ key }`, function ( event ) {
				checkoutObject.#dafValidationFields[ key ] =
					event.target.value !== '';
				checkoutObject.#areAllDAFFieldsValid( 'inputs' );
			} );
		}

		jQuery( document ).on(
			'change',
			'#billing_country',
			function ( event ) {
				const selectedValue = jQuery( this ).val();
				checkoutObject.#dafValidationFields.billing_country =
					selectedValue && selectedValue === 'US';
				checkoutObject.#areAllDAFFieldsValid( 'country' );
			}
		);
		jQuery( document ).on( 'change', '#billing_state', function ( event ) {
			const selectedValue = jQuery( this ).val();
			checkoutObject.#dafValidationFields.billing_state =
				selectedValue && selectedValue !== '';
			checkoutObject.#areAllDAFFieldsValid( 'state' );
		} );
	}

	#areAllDAFFieldsValid( except = '' ) {
		if ( except !== 'inputs' ) {
			this.validateDAFInputs();
		}

		this.validateBillingCountry();
		this.validateBillingState();

		const is_valid = Object.values( this.#dafValidationFields ).every(
			( value ) => value === true
		);

		this.toggleCheckoutSubmitButtonForDAF( ! is_valid );
		return is_valid;
	}

	toggleCheckoutSubmitButtonForDAF( disableSubmit ) {
		const submitButton = document.getElementById('place_order');
		const paymentSection = document.querySelector('#payment');
		const isZeroTotalCoupon = paymentSection?.getAttribute('data-zero-total-coupon') === '1';
		if ( isZeroTotalCoupon ) {
			submitButton?.removeAttribute('disabled');
			return;
		}
		const paymentMethodsList = document.querySelector('.wc_payment_methods');
		if (
			paymentMethodsList?.classList.contains(
				'wc_payment_methods_empty'
			) &&
			isZeroTotalCoupon
		) {
			submitButton?.removeAttribute( 'disabled' );
			return;
		}
		const checkedPaymentMethod = document.querySelector('input[name=payment_method]:checked');
		if(checkedPaymentMethod && checkedPaymentMethod.value !== '__levit_dafpay') {
			submitButton?.removeAttribute('disabled');
			return;
		}

		if (disableSubmit) {
			submitButton?.setAttribute('disabled', 'disabled');
		} else {
			submitButton?.removeAttribute('disabled');
		}
	}

	buttonDisableFix() {
		const predefinedAmounts = document.querySelectorAll(
			'input[name="predefined_amount"]'
		);
		predefinedAmounts.forEach( ( radio ) => {
			radio.addEventListener( 'change', ( event ) => {
				const paymentsContainer = document.querySelector(
					'.woocommerce-checkout-payment'
				);
				if ( paymentsContainer ) {
					const paymentMethods =
						document.querySelector( '.payment_methods' );
					const selectedInput = paymentMethods.querySelector(
						'input[type="radio"]:checked'
					);
					const placeOrderButton = document.querySelector(
						'button[name="woocommerce_checkout_place_order"]'
					);
					if (
						selectedInput === null ||
						selectedInput !== '__levit_dafpay'
					) {
						placeOrderButton.removeAttribute( 'disabled' );
					}
				}
			} );
		} );
	}

	chooseCreditCardFirst() {
		const paymentMethods = document.querySelector( '.payment_methods' );
		if ( paymentMethods ) {
			const levitStripeInput = paymentMethods.querySelector(
				'input[value="levit_stripe"]'
			);
			const levitAuthorizeNetInput = paymentMethods.querySelector(
				'input[value="levit_authorize_net"]'
			);
			if ( levitStripeInput ) {
				levitStripeInput.checked = true;
			} else if ( levitAuthorizeNetInput ) {
				levitAuthorizeNetInput.checked = true;
			}
		}
	}

	syncCampaignMessageCounter(){
		document.querySelector("#campaign_message")?.addEventListener('input', (e) => {
			const messageTextCounter = document.querySelector(".campaign_message_text_counter")
			if(messageTextCounter) messageTextCounter.innerText = `${e.target.value.trim().length}/200`
		})
	}
}
